import isPlainObject from 'lodash/isPlainObject';

import type { EvictorLabelSelectorExpression } from '@cast/types-workload-eviction';

import {
  EVICTION_CONFIG_SETTING_KEYS,
  KINDS,
  LABEL_OPERATORS,
  RULE_PRESET_BY_TYPE,
} from '../_constants';
import type {
  BuilderRule,
  EvictionConfigSettingKey,
  Kind,
  LabelOperator,
  Result,
  RuleType,
} from '../_types';

import { parseYaml } from './advancedConfig';
import { isNodeRuleType } from './builder';
import isNumber from 'lodash/isNumber';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  isPlainObject(value);

const isRuleType = (value: string): value is RuleType =>
  value in RULE_PRESET_BY_TYPE;

const resolveRuleType = ({
  selectorType,
  entrySetting,
}: {
  selectorType: 'pod' | 'node';
  entrySetting: EntrySetting;
}): RuleType | undefined => {
  return Object.keys(RULE_PRESET_BY_TYPE)
    .filter(isRuleType)
    .find((ruleType) => {
      const preset = RULE_PRESET_BY_TYPE[ruleType];
      const presetSelectorType = isNodeRuleType(ruleType) ? 'node' : 'pod';

      return (
        presetSelectorType === selectorType &&
        preset.settingKey === entrySetting.settingKey &&
        preset.enabled === entrySetting.enabled
      );
    });
};

const isSupportedOperator = (operator: unknown): operator is LabelOperator => {
  return (
    typeof operator === 'string' &&
    LABEL_OPERATORS.some((supportedOperator) => supportedOperator === operator)
  );
};

const isSupportedKind = (kind: string): kind is Kind => {
  return KINDS.some((supportedKind) => supportedKind === kind);
};

const isEvictionConfigSettingKey = (
  key: string
): key is EvictionConfigSettingKey => {
  return EVICTION_CONFIG_SETTING_KEYS.some((settingKey) => settingKey === key);
};

const parseMatchLabels = (
  matchLabels: unknown
): Result<EvictorLabelSelectorExpression[], string> => {
  if (!isRecord(matchLabels) || Object.keys(matchLabels).length === 0) {
    return { success: true, value: [] };
  }

  const expressions: EvictorLabelSelectorExpression[] = [];

  for (const [key, value] of Object.entries(matchLabels)) {
    if (typeof value !== 'string') {
      return {
        success: false,
        error: `matchLabels "${key}" must be a string`,
      };
    }

    expressions.push({ key, operator: 'IN', values: [value] });
  }

  return { success: true, value: expressions };
};

const parseMatchExpression = (
  matchExpression: unknown
): Result<EvictorLabelSelectorExpression, string> => {
  if (!isRecord(matchExpression)) {
    return {
      success: false,
      error: 'matchExpressions entry must be an object',
    };
  }

  const { key, operator, values } = matchExpression;

  if (typeof key !== 'string') {
    return {
      success: false,
      error: 'matchExpressions entry key must be a string',
    };
  }

  if (!isSupportedOperator(operator)) {
    return {
      success: false,
      error: 'matchExpressions entry has unsupported operator',
    };
  }

  const parsedExpression: EvictorLabelSelectorExpression = {
    key,
    operator,
  };

  if (!Array.isArray(values)) {
    return { success: true, value: parsedExpression };
  }

  const isValuesValid = values.every((value) => typeof value === 'string');

  if (!isValuesValid) {
    return {
      success: false,
      error: 'matchExpressions values must be strings',
    };
  }

  return { success: true, value: { ...parsedExpression, values } };
};

const parseMatchExpressions = (
  matchExpressions: unknown
): Result<EvictorLabelSelectorExpression[], string> => {
  if (!Array.isArray(matchExpressions) || matchExpressions.length === 0) {
    return { success: true, value: [] };
  }

  const expressions: EvictorLabelSelectorExpression[] = [];

  for (const expression of matchExpressions) {
    const parseMatchExpressionResult = parseMatchExpression(expression);
    if (!parseMatchExpressionResult.success) {
      return parseMatchExpressionResult;
    }

    expressions.push(parseMatchExpressionResult.value);
  }

  return { success: true, value: expressions };
};

const parseLabelSelector = (
  labelSelector: unknown
): Result<EvictorLabelSelectorExpression[] | undefined, string> => {
  if (!labelSelector) {
    return { success: true, value: undefined };
  }

  if (!isRecord(labelSelector)) {
    return { success: false, error: 'labelSelector must be an object' };
  }

  const { matchLabels, matchExpressions } = labelSelector;
  const parsedMatchLabelsResult = parseMatchLabels(matchLabels);

  if (!parsedMatchLabelsResult.success) {
    return parsedMatchLabelsResult;
  }

  const parsedMatchExpressionsResult = parseMatchExpressions(matchExpressions);

  if (!parsedMatchExpressionsResult.success) {
    return parsedMatchExpressionsResult;
  }

  const combinedLabelsSelectorExpressions: EvictorLabelSelectorExpression[] = [
    ...parsedMatchLabelsResult.value,
    ...parsedMatchExpressionsResult.value,
  ];

  return { success: true, value: combinedLabelsSelectorExpressions };
};

type EntrySetting = {
  settingKey: EvictionConfigSettingKey;
  enabled: boolean;
};

const parseEntrySettings = (
  settings: unknown
): Result<EntrySetting | undefined, string> => {
  if (!settings) {
    return { success: true, value: undefined };
  }

  if (!isRecord(settings)) {
    return { success: false, error: 'settings must be an object' };
  }

  const settingKeys = Object.keys(settings).filter(isEvictionConfigSettingKey);

  if (settingKeys.length === 0) {
    return { success: false, error: 'settings must have at least one key' };
  }

  if (settingKeys.length > 1) {
    return { success: false, error: 'settings must have at most one key' };
  }

  const settingKey = settingKeys[0];

  const setting = settings[settingKey];

  if (!isRecord(setting)) {
    return { success: false, error: `setting ${settingKey} must be an object` };
  }

  const enabled = !!setting.enabled;

  return {
    success: true,
    value: { settingKey, enabled },
  };
};

const parseKind = ({
  kind,
  index,
}: {
  kind: unknown;
  index: number;
}): Result<Kind | undefined, string> => {
  if (!kind) {
    return { success: true, value: undefined };
  }

  if (typeof kind !== 'string') {
    return {
      success: false,
      error: `Rule ${index}: kind must be a string`,
    };
  }

  if (!isSupportedKind(kind)) {
    return {
      success: false,
      error: `Rule ${index}: kind "${kind}" is not supported in Builder`,
    };
  }

  return { success: true, value: kind };
};

const parseNamespace = ({
  namespace,
  index,
}: {
  namespace: unknown;
  index: number;
}): Result<string | undefined, string> => {
  if (!namespace) {
    return { success: true, value: undefined };
  }

  if (typeof namespace !== 'string') {
    return {
      success: false,
      error: `Rule ${index}: namespace must be a string`,
    };
  }

  return {
    success: true,
    value: namespace,
  };
};

const parseReplicasMin = ({
  replicasMin,
  index,
}: {
  replicasMin: unknown;
  index: number;
}): Result<number | undefined, string> => {
  if (replicasMin === undefined) {
    return { success: true, value: undefined };
  }

  if (!isNumber(replicasMin)) {
    return {
      success: false,
      error: `Rule ${index}: replicasMin must be a number`,
    };
  }

  return { success: true, value: replicasMin };
};

const parsePodSelector = ({
  podSelector,
  ruleType,
  index,
}: {
  podSelector: unknown;
  ruleType: RuleType;
  index: number;
}): Result<BuilderRule, string> => {
  if (!isRecord(podSelector)) {
    return {
      success: false,
      error: `Rule ${index}: podSelector must be an object`,
    };
  }

  const { kind, namespace, replicasMin, labelSelector } = podSelector;

  const kindResult = parseKind({ kind, index });
  if (!kindResult.success) {
    return kindResult;
  }

  const namespaceResult = parseNamespace({ namespace, index });
  if (!namespaceResult.success) {
    return namespaceResult;
  }

  const replicasMinResult = parseReplicasMin({ replicasMin, index });
  if (!replicasMinResult.success) {
    return replicasMinResult;
  }

  const parseLabelSelectorResult = parseLabelSelector(labelSelector);

  if (!parseLabelSelectorResult.success) {
    return parseLabelSelectorResult;
  }

  return {
    success: true,
    value: {
      ruleType,
      namespace: namespaceResult.value,
      kind: kindResult.value,
      replicasMin: replicasMinResult.value,
      labels: parseLabelSelectorResult.value,
    },
  };
};

const parseNodeSelector = ({
  nodeSelector,
  ruleType,
  index,
}: {
  nodeSelector: unknown;
  ruleType: RuleType;
  index: number;
}): Result<BuilderRule, string> => {
  if (!nodeSelector) {
    return { success: true, value: {} };
  }

  if (!isRecord(nodeSelector)) {
    return {
      success: false,
      error: `Rule ${index}: nodeSelector must be an object`,
    };
  }

  const parseLabelSelectorResult = parseLabelSelector(
    nodeSelector.labelSelector
  );
  if (!parseLabelSelectorResult.success) {
    return parseLabelSelectorResult;
  }

  return {
    success: true,
    value: {
      ruleType,
      labels: parseLabelSelectorResult.value,
    },
  };
};

const parseConfigEntry = (
  evictionConfigEntry: unknown,
  index: number
): Result<BuilderRule, string> => {
  if (!isRecord(evictionConfigEntry)) {
    return { success: false, error: `Rule ${index}: must be an object` };
  }

  const { podSelector, nodeSelector, settings } = evictionConfigEntry;
  const hasPodSelector = !!podSelector;
  const hasNodeSelector = !!nodeSelector;

  if (hasPodSelector && hasNodeSelector) {
    return {
      success: false,
      error: `Rule ${index}: has both podSelector and nodeSelector`,
    };
  }

  if (!hasPodSelector && !hasNodeSelector) {
    return {
      success: true,
      value: {},
    };
  }

  const parsedSettingsResult = parseEntrySettings(settings);

  if (!parsedSettingsResult.success) {
    return {
      success: false,
      error: `Rule ${index}: ${parsedSettingsResult.error}`,
    };
  }

  if (!parsedSettingsResult.value) {
    return {
      success: true,
      value: {},
    };
  }

  const ruleType = resolveRuleType({
    selectorType: hasPodSelector ? 'pod' : 'node',
    entrySetting: parsedSettingsResult.value,
  });

  if (!ruleType) {
    return {
      success: false,
      error: `Rule ${index}: unrecognized selector/setting combination`,
    };
  }

  if (hasPodSelector) {
    return parsePodSelector({ podSelector, ruleType, index });
  }

  return parseNodeSelector({ nodeSelector, ruleType, index });
};

const parsedYamlToBuilderRules = (
  parsedYaml: unknown
): Result<BuilderRule[], string[]> => {
  if (!parsedYaml) {
    return { success: true, value: [] };
  }

  if (!isRecord(parsedYaml)) {
    return { success: false, error: ['Root must be an object'] };
  }

  const { evictionConfig } = parsedYaml;

  if (!evictionConfig) {
    return { success: true, value: [] };
  }

  if (!Array.isArray(evictionConfig)) {
    return { success: false, error: ['evictionConfig must be an array'] };
  }

  const rules: BuilderRule[] = [];
  const blockers: string[] = [];

  evictionConfig.forEach((evictionConfigEntry, index) => {
    const result = parseConfigEntry(evictionConfigEntry, index);

    if (!result.success) {
      blockers.push(result.error);
    } else {
      rules.push(result.value);
    }
  });

  if (blockers.length > 0) {
    return { success: false, error: blockers };
  }

  return { success: true, value: rules };
};

/**
 * Parses a raw YAML string into the Builder's internal rule model, acting as the
 * representability gate between YAML and Builder mode.
 *
 * The Builder can only express a closed subset of what the evictionConfig YAML
 * allows, so the YAML to Builder direction is not always safe. This function decides
 * whether a given config can be edited in the Builder at all
 */
export const parseYamlToBuilderRules = (
  yamlString: string
): Result<BuilderRule[], string[]> => {
  const yamlResult = parseYaml(yamlString);

  if (!yamlResult.success) {
    return { success: false, error: ['Invalid YAML syntax'] };
  }

  return parsedYamlToBuilderRules(yamlResult.value);
};
